import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import dmsToDecimal from "@/utils/dmsToDecimal";
import moment from "moment";
import { handleDuration } from "helper/duration";
import { BOOKING_TYPE } from "../constants/variables";
import { fetchCMSMasterData } from "../dataLayer/cms";
import { getLinks } from "../dataLayer/getlinks";
import { getActiveAirport } from "../dataLayer/listOfFlights";
import { fetchMasterData } from "../dataLayer/masterData";
import { getResources } from "../dataLayer/resource";
import { filterAirportsWithPopularCities } from "../services/airportDetails/filterAirport";
import { useSelector } from "react-redux";

export const CommonDataContext = createContext<any>({});

export const useCommonContext = () => useContext(CommonDataContext);

interface FlightData {
  originCode: string;
  origin: {
    stationCode: string;
    cityName: string;
    stationName: string;
    coordinates: {
      latitude: string;
      longitude: string;
    };
  };
  destination: {
    stationCode: string;
    cityName: string;
    stationName: string;
    coordinates: {
      latitude: string;
      longitude: string;
    };
  };
  via: {
    stationCode: string;
    cityName: string;
    stationName: string;
    coordinates: {
      latitude: string;
      longitude: string;
    };
  }[];
  flights: string[];
  departure: string;
  arrival: string;
  duration: string;
}

interface LatLng {
  lat: number;
  lng: number;
}

interface Route {
  coordinates: LatLng[];
}

function CommonDataContextProvider({ children }: { children: React.ReactNode }) {
  const [selectedBusinessProfile, setSelectedBusinessProfile] = useState({});
  const [tripType, setTripType] = useState(BOOKING_TYPE.ONE_WAY);
  const [selectedSpecialFare, setSelectedSpecialFare] = useState({});
  const [specialFareForPax, setSpecialFareForPax] = useState(""); // for retaining the selected special fare in the UI
  const [prevSpecialFareForPax, setPrevSpecialFareForPax] = useState({
    current: "",
    prev: ""
  });
  const [login, setLogin] = useState(false);
  const [ready, setReady] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("INR"); // default value
  const [selectedCurrencyDetails, setSelectedCurrencyDetails] = useState<any>({});
  const [commonMasterData, setCommonMasterData] = useState({});
  const [translations, setTranslations] = useState({});
  const [interactiveNetworkData, setInteractiveNetworkData] = useState<FlightData[]>([]);
  const routesData = useMemo<Record<string, Route[]>>(() => ({}), []);
  const sideBarData = useMemo<Record<string, FlightData[]>>(() => ({}), []);
  const deeplinkCurrencyChange = useSelector((state:any)=>state.persistedReducers.booking.deeplinkCurrencyChange)

  async function getResourceData() {
    const resource = await getResources();
    setTranslations(resource);
  }
  const getMasterData = useCallback(async () => {
    try {
      const response = await fetchMasterData();
      const cmsMasterData = await fetchCMSMasterData();
      const linkData = await getLinks();
      const popularCities = cmsMasterData?.data?.story?.content?.popular_cities;
      const allMarketAvailability = await getActiveAirport();

      const activeCities = response.data.cities?.values;
      const cityMap = new Map(activeCities.map((city) => [city.cityCode, city]));
      const activeAirports = response?.data?.stations?.values;
      const stationWithCityInfo = activeAirports.map((station) => ({
        ...station,
        cityInfo: cityMap.get(station.locationDetails.cityCode) || {}
      }));
      const filteredAirportsWithPopularCities = filterAirportsWithPopularCities(stationWithCityInfo, popularCities)
      const common = {
        airports: stationWithCityInfo,
        allMarketAvailability,
        cmsConfig: cmsMasterData?.data?.story?.content?.config?.[0],
        countryList: response?.data?.country?.values,
        currencies: response?.data?.currencies,
        documentType: response?.data?.documentTypes?.values?.filter((type: any) => type.documentTypeCode.includes("P")),
        genderTypes: response?.data?.titles?.values,
        links: linkData,
        passengerTypes: response?.data?.passengerTypes?.values,
        popularAirports: filteredAirportsWithPopularCities,
        webConfig: cmsMasterData?.data?.story?.content?.webConfig?.[0],
        upgradeCards: cmsMasterData?.data?.story?.content?.upgradeCards?.[0],
        promoCodes: cmsMasterData?.data?.story?.content?.promoCodesAndOffers?.[0]?.promoCodes,
        cities : response?.data?.cities?.values,
        infantAge: cmsMasterData?.data?.story?.content?.infantAge?.[0],
      };
      setCommonMasterData(common);

      setReady(true);
    } catch (error) {
      console.warn(error);
    }
  }, []);

  const getSelectedCurrencyDetails = () => {
    if(deeplinkCurrencyChange){
      setSelectedCurrency(deeplinkCurrencyChange)
      const selectedCurrencyData = commonMasterData?.currencies?.values?.find((currency: any) => currency?.currencyCode === deeplinkCurrencyChange)
      setSelectedCurrencyDetails(selectedCurrencyData);
      return;
    } else { 
    const selectedCurrencyData = commonMasterData?.currencies?.values?.find((currency: any) => currency?.currencyCode === selectedCurrency)
    setSelectedCurrencyDetails(selectedCurrencyData)
    }
  }
  const indexDataByOriginCode = () =>
    interactiveNetworkData.reduce((index: { [originCode: string]: FlightData[] }, flight: FlightData) => {
      const newIndex = { ...index };
      if (!newIndex[flight.originCode]) {
        newIndex[flight.originCode] = [];
      }
      newIndex[flight.originCode].push(flight);
      return newIndex;
    }, {});

  const getDataByOriginCode = (code: string): FlightData[] => {
    const indexedData = indexDataByOriginCode();
    return indexedData[code] || [];
  };

  const getRoutes = (selectedStationData: FlightData[]): { coordinates: [number, number][] }[] => selectedStationData.map((flight) => ({
      coordinates: [
        [dmsToDecimal(flight.origin.coordinates.latitude), dmsToDecimal(flight.origin.coordinates.longitude)],
        ...flight.via.map((via) => [dmsToDecimal(via.coordinates.latitude), dmsToDecimal(via.coordinates.longitude)]),
        [dmsToDecimal(flight.destination.coordinates.latitude), dmsToDecimal(flight.destination.coordinates.longitude)]
      ] as [number, number][]
    }));

  const computeInteractiveMapRoutes = async () => {
    if(!interactiveNetworkData?.length)
      return; // If no interactiveNetworkData, return

    if (Object.keys(routesData).length > 0 && Object.keys(sideBarData).length > 0) {
      return; // If already computed, return without re-computation
    }

    const uniqueOriginCodes = [...new Set(interactiveNetworkData.map((item) => item.originCode))];
    uniqueOriginCodes.forEach((originCode) => {
      const selectedStationData = interactiveNetworkData.filter((item) => item.originCode === originCode);
      const routes = getRoutes(selectedStationData);
      routesData[originCode] = routes;
      const dataByOriginCode = getDataByOriginCode(originCode);
      // Sort dataByOriginCode based on departure times in minutes and duration
      dataByOriginCode.sort((a, b) => {
        const departureMinutesA = moment.duration(a.departure).asMinutes();
        const departureMinutesB = moment.duration(b.departure).asMinutes();

        if (departureMinutesA !== departureMinutesB) {
          return departureMinutesA - departureMinutesB;
        }
        // If departure times are the same, compare durations
        const durationMinutesA = moment.duration(handleDuration(a.duration)).asMinutes();
        const durationMinutesB = moment.duration(handleDuration(b.duration)).asMinutes();
        return durationMinutesA - durationMinutesB;
      });
      sideBarData[originCode] = dataByOriginCode;
    });

  };

  useEffect(() => {
    getMasterData();
    getResourceData();
  }, [getMasterData]);

  useEffect(() => {
    getSelectedCurrencyDetails()
  }, [selectedCurrency, commonMasterData, deeplinkCurrencyChange]);

  useEffect(()=>{
      computeInteractiveMapRoutes();
  },[interactiveNetworkData])

  const {
    airports,
    allMarketAvailability,
    cmsConfig,
    countryList,
    currencies,
    documentType,
    genderTypes,
    links,
    navData,
    passengerTypes,
    popularAirports,
    webConfig,
    upgradeCards,
    promoCodes,
    cities,
    infantAge
  } = commonMasterData;

  const values = useMemo(
    () => ({
      cities,
      airports,
      allMarketAvailability,
      appConfig: cmsConfig,
      availableCurrencies: currencies,
      commonNavFooter: navData,
      configData: cmsConfig,
      countryList,
      documentType,
      genderTypes,
      getResourceData,
      links,
      login,
      passengerTypes,
      popularAirports,
      prevSpecialFareForPax,
      ready,
      resources: translations,
      selectedBusinessProfile,
      selectedCurrency,
      selectedCurrencyDetails,
      selectedSpecialFare,
      setLogin,
      setPrevSpecialFareForPax,
      setSelectedBusinessProfile,
      setSelectedCurrency,
      setSelectedSpecialFare,
      setSpecialFareForPax,
      setTripType,
      specialFareForPax,
      tripType,
      webConfig,
      upgradeCards,
      promoCodes,
      interactiveNetworkData,
      setInteractiveNetworkData,
      routesData,
      sideBarData,
      computeInteractiveMapRoutes,
      infantAge
    }),
    [
      airports,
      allMarketAvailability,
      cmsConfig,
      countryList,
      currencies,
      documentType,
      genderTypes,
      links,
      login,
      navData,
      passengerTypes,
      popularAirports,
      prevSpecialFareForPax,
      ready,
      selectedBusinessProfile,
      selectedCurrency,
      selectedCurrencyDetails,
      selectedSpecialFare,
      specialFareForPax,
      translations,
      tripType,
      webConfig,
      upgradeCards,
      promoCodes,
      cities,
      infantAge
    ]
  );

  return <CommonDataContext.Provider value={values}>{children}</CommonDataContext.Provider>;
}

export default CommonDataContextProvider;
